import { useEffect, useState } from "react";
import { Eye, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function SmsRoutingMatrix() {
  const [roles, setRoles] = useState([]);
  const [events, setEvents] = useState([]);
  const [matrix, setMatrix] = useState({});  // key = `${eventKey}|${roleName}` → bool
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ev, mx] = await Promise.all([
        api.get("/roles"),
        api.get("/sms/event-types"),
        api.get("/sms/routing"),
      ]);
      setRoles(r.data.roles || []);
      setEvents((ev.data.event_types || []).filter(e => e.is_active));
      const m = {};
      (mx.data.matrix || []).forEach(cell => {
        m[`${cell.event_key}|${cell.role_name}`] = !!cell.enabled;
      });
      setMatrix(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load routing");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (event_key, role_name) => {
    const key = `${event_key}|${role_name}`;
    const next = !matrix[key];
    setMatrix({ ...matrix, [key]: next });
    try {
      await api.put("/sms/routing", { event_key, role_name, enabled: next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      setMatrix({ ...matrix, [key]: !next });
    }
  };

  const preview = async (event_key) => {
    setPreviewing(event_key);
    setPreviewData(null);
    try {
      const r = await api.get(`/sms/events/${event_key}/audience`);
      setPreviewData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      setPreviewing(null);
    }
  };

  if (loading) return <div className="p-6 text-center text-[#A3A3A3] text-xs">Loading routing…</div>;

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="sms-routing-matrix">
      <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5]">
        <h3 className="text-sm font-bold text-[#0A0A0A]">SMS Routing Rules</h3>
        <p className="text-[10px] text-[#A3A3A3] mt-0.5">Tick a cell to send this event type to everyone in that role group. Users can still opt out individually from their Profile page.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
              <th className="px-3 py-2 text-left font-bold sticky left-0 bg-[#FAFAFA] z-10 min-w-[220px]">Event</th>
              {roles.map(r => (
                <th key={r.id} className="px-3 py-2 text-center font-bold min-w-[110px]">{r.label}</th>
              ))}
              <th className="px-3 py-2 text-center font-bold">Preview</th>
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.id} className="border-b border-[#F5F5F5]" data-testid={`routing-row-${ev.key_name}`}>
                <td className="px-3 py-2 sticky left-0 bg-white z-10">
                  <div className="font-bold text-[#0A0A0A]">{ev.label}</div>
                  <div className="text-[10px] text-[#A3A3A3] mt-0.5">{ev.description}</div>
                </td>
                {roles.map(r => {
                  const key = `${ev.key_name}|${r.name}`;
                  const on = !!matrix[key];
                  return (
                    <td key={r.id} className="px-3 py-2 text-center">
                      <button
                        onClick={() => toggle(ev.key_name, r.name)}
                        className={`w-8 h-8 border flex items-center justify-center transition-colors ${on ? "bg-[#0055FF] border-[#0055FF] text-white hover:bg-[#003ECC]" : "bg-white border-[#E5E5E5] text-[#A3A3A3] hover:border-[#0055FF]"}`}
                        data-testid={`routing-cell-${ev.key_name}-${r.name}`}
                        title={on ? "Remove" : "Add"}
                      >
                        {on && <CheckCircle size={14} weight="fill" />}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center">
                  <Button variant="outline" size="sm" onClick={() => preview(ev.key_name)} className="rounded-none h-7 text-[10px] uppercase tracking-wider" data-testid={`preview-${ev.key_name}`}>
                    <Eye size={12} className="mr-1" /> Preview
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview modal */}
      {previewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPreviewing(null)}>
          <div className="bg-white border border-[#E5E5E5] p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Eye size={18} className="text-[#0055FF]" />
              <h3 className="text-lg font-black text-[#0A0A0A]">Audience Preview — <span className="font-mono text-sm text-[#525252]">{previewing}</span></h3>
            </div>
            {!previewData ? (
              <div className="text-center text-[#A3A3A3] py-8 text-xs">Loading…</div>
            ) : previewData.count === 0 ? (
              <div className="bg-[#FFFBEB] border border-[#FDE68A] p-4 flex gap-2 items-start">
                <Warning size={18} className="text-[#D97706] flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <div className="font-bold text-[#D97706]">No recipients</div>
                  <div className="text-[#525252] mt-1">No user's roles are routed to this event, or no user has opted in. Edit the matrix above or ask users to set their phone + tick "Send me SMS alerts" in their Profile.</div>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-xs text-[#525252] mb-3">If this event fired right now, <strong>{previewData.count}</strong> user{previewData.count !== 1 ? "s" : ""} would be texted:</p>
                <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5] max-h-80 overflow-y-auto">
                  {previewData.recipients.map(u => (
                    <div key={u.user_id} className="px-3 py-2 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-[#0A0A0A]">{u.name}</div>
                        <div className="text-[#A3A3A3] text-[10px]">{u.email}</div>
                      </div>
                      <div className="font-mono text-[11px] text-[#525252]">{u.phone}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button onClick={() => setPreviewing(null)} className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] text-white text-xs" data-testid="preview-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
